import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useRef } from "react";
import { XMarkIcon, ExclamationTriangleIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  setIsVisibleMappedClinic?: (value: boolean) => void; // 👈 add this
  setInitialState?: (value: boolean) => void;
  invoiceId: string;
  invoiceType: string;
  clinic_details: any;
  buttonSource: any;
  source: string;
  onContinue: () => void;
};

const InvoiceReturnClinicModalComponent: React.FC<Props> = ({
  open,
  setOpen,
  clinic_details,
  onContinue,
  buttonSource,
  setIsVisibleMappedClinic,
}) => {
  const cancelButtonRef = useRef(null);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const isCancel = buttonSource === "CANCEL";

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-md">
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <div className="p-6">
                  <div
                    className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${
                      isCancel ? "bg-red-50" : "bg-amber-50"
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className={`h-6 w-6 ${isCancel ? "text-red-500" : "text-amber-500"}`}
                    />
                  </div>

                  <div className="mt-4 text-center">
                    <h2 className="text-base font-semibold text-gray-900">
                      {isCancel ? "Invoice cannot be cancelled" : "Clinics don’t match"}
                    </h2>
                    <p className="mt-1.5 text-sm text-gray-500">
                      {isCancel
                        ? "The billed clinic and your selected clinic do not match. Switch to the billed clinic to cancel this invoice."
                        : "Proceed only if the physical product is received in the selected clinic."}
                    </p>
                  </div>

                  <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-xs font-medium text-gray-500">
                        <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                        Billed Clinic
                      </span>
                      <span className="text-sm font-semibold text-gray-900 text-right">
                        {clinic_details?.name || "—"}
                      </span>
                    </div>
                    <div className="my-3 border-t border-dashed border-gray-200" />
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-xs font-medium text-gray-500">
                        <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                        Selected Clinic
                      </span>
                      <span className="text-sm font-semibold text-gray-900 text-right">{mappedClinicName || "—"}</span>
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    {isCancel ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="flex-1 rounded-lg bg-[#72767f1a] px-4 py-2.5 text-sm font-medium text-[#000000] hover:bg-[#72767f2a]"
                        >
                          Go Back
                        </button>
                        <button
                          type="submit"
                          className="flex-1 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                          onClick={() => {
                            setOpen(false);
                            setIsVisibleMappedClinic?.(true);
                          }}
                        >
                          Change Clinic
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={onContinue}
                          className="flex-1 rounded-lg bg-[#72767f1a] px-4 py-2.5 text-sm font-medium text-[#000000] hover:bg-[#72767f2a]"
                        >
                          Continue
                        </button>
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="flex-1 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                        >
                          Go Back
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default InvoiceReturnClinicModalComponent;
